// Botão "Sincronizar com OMIE" das despesas (Fase 2): busca o Contas a Pagar com EMISSÃO entre
// os meses escolhidos, classifica pelo código do item (descrição da categoria) e pelo de-para de
// departamentos. Roda em segundo plano no backend (services/realizado_despesas_omie.py); aqui
// acompanhamos o status a cada 4s.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { getDespesasOmieStatus, sincronizarDespesasOmie } from "../../api";
import { AvisoErro, Badge, Card, botaoPequeno, formatCompetencia, formatData, inputStyle } from "./comum";

const hoje = new Date();
const MES_ATUAL = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`;

// "2026-09" → ["2026-09-01", "2026-09-30"]
export function limitesDoMes(mes) {
  const [a, m] = mes.split("-").map(Number);
  const ultimo = new Date(a, m, 0).getDate();
  return [`${mes}-01`, `${mes}-${String(ultimo).padStart(2, "0")}`];
}

export default function SincronizacaoDespesas({ C, onConcluido }) {
  const navigate = useNavigate();
  const [execucao, setExecucao] = useState(null);
  const [mesDe, setMesDe] = useState(MES_ATUAL);
  const [mesAte, setMesAte] = useState(MES_ATUAL);
  const [erro, setErro] = useState("");
  const statusAnterior = useRef(null);

  const consultar = useCallback(() => getDespesasOmieStatus().then(setExecucao).catch((e) => setErro(e.message)), []);

  useEffect(() => { consultar(); }, [consultar]);
  useEffect(() => {
    if (execucao?.status !== "EXECUTANDO") return undefined;
    const t = setInterval(consultar, 4000);
    return () => clearInterval(t);
  }, [execucao?.status, consultar]);
  useEffect(() => {
    if (statusAnterior.current === "EXECUTANDO" && execucao?.status === "OK") onConcluido?.();
    statusAnterior.current = execucao?.status;
  }, [execucao?.status, onConcluido]);

  const iniciar = async () => {
    setErro("");
    if (!mesDe || !mesAte || mesAte < mesDe) { setErro("Escolha o mês inicial e o final (final ≥ inicial)."); return; }
    try {
      setExecucao(await sincronizarDespesasOmie(limitesDoMes(mesDe)[0], limitesDoMes(mesAte)[1]));
    } catch (e) { setErro(e.message); }
  };

  const executando = execucao?.status === "EXECUTANDO";
  const totais = Object.values(execucao?.resumo?.empresas || {}).reduce((acc, e) => {
    ["titulos", "novos", "atualizados", "removidos", "baixasIgnoradas", "valorTitulos"].forEach((k) => { acc[k] = (acc[k] || 0) + (e[k] || 0); });
    return acc;
  }, {});
  const pendencias = Object.values(execucao?.resumo?.erros || {}).reduce((a, b) => a + b, 0);
  const p = execucao?.parametros || {};
  const campoMes = { ...inputStyle(C), minHeight: 34, padding: "5px 8px", fontSize: 12, width: "auto" };

  return (
    <Card
      C={C}
      titulo="Sincronizar com OMIE"
      subtitulo="Busca o Contas a Pagar das 4 empresas com emissão nos meses escolhidos. Classifica pelo código do item que está na descrição da categoria e pelo de-para de departamentos. O pagamento de um título (baixa) não entra de novo."
      acoes={
        <>
          <input type="month" aria-label="Mês inicial" value={mesDe} onChange={(e) => setMesDe(e.target.value)} disabled={executando} style={campoMes} />
          <span style={{ fontSize: 12, color: C.muted }}>até</span>
          <input type="month" aria-label="Mês final" value={mesAte} onChange={(e) => setMesAte(e.target.value)} disabled={executando} style={campoMes} />
          <button style={{ ...botaoPequeno(C.brand), opacity: executando ? 0.6 : 1 }} disabled={executando} onClick={iniciar}>
            <RefreshCw size={13} style={executando ? { animation: "spin 1s linear infinite" } : undefined} />
            {executando ? "Sincronizando…" : "Sincronizar agora"}
          </button>
        </>
      }
    >
      <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
      <AvisoErro C={C}>{erro}</AvisoErro>
      {!execucao ? (
        <div style={{ fontSize: 12, color: C.muted }}>Nenhuma sincronização feita ainda.</div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", fontSize: 12 }}>
          <Badge cor={execucao.status === "OK" ? C.success : execucao.status === "ERRO" ? C.danger : C.warning}>
            {{ OK: "Concluída", ERRO: "Erro", EXECUTANDO: "Em andamento" }[execucao.status] || execucao.status}
          </Badge>
          <span style={{ color: C.muted }}>
            {formatData(execucao.iniciadoEm)} {execucao.iniciadoEm.slice(11, 16)} · {execucao.usuario}
            {p.de && ` · emissão ${formatCompetencia(p.de)} a ${formatCompetencia(p.ate)}`}{p.empresa ? ` · ${p.empresa}` : ""}
          </span>
          {execucao.status === "OK" && (
            <>
              <span><b>{totais.titulos}</b> títulos</span>
              <span><b>{totais.novos}</b> novos · <b>{totais.atualizados}</b> atualizados{totais.removidos ? <> · <b>{totais.removidos}</b> removidos no Omie</> : null}</span>
              <span style={{ color: C.muted }}>{totais.baixasIgnoradas} pagamentos (baixas) ignorados</span>
              {pendencias > 0 && (
                <button onClick={() => navigate("/gestao-realizado/log-erros")} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", fontSize: 12, fontWeight: 700, padding: 0 }}>
                  {pendencias} pendência(s) no log →
                </button>
              )}
            </>
          )}
          {execucao.status === "ERRO" && <span style={{ color: C.danger }}>{(execucao.erro || "").split("\n")[0]}</span>}
        </div>
      )}
    </Card>
  );
}
